import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react'
import {
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  propsDataLocationToPathAndValue,
} from '@ea-lab/reactive-json'
import { ChartJs } from './ChartJs'
import { shapeFromSpec, evaluateKpi, getPath } from '../../../dataProcessors/esAggToChart'
import { crmDashboardProcessor } from '../../../dataProcessors/crmDashboardProcessor'

/**
 * Chargeur générique « agrégation Elasticsearch → graphe / KPIs », à fetch
 * value-stable.
 *
 * OBJECTIF : un nouveau graphe de tableau de bord = une requête ES (config ou
 * entité) + un bloc YAML. **Aucun JS à écrire.** Ce composant ne connaît ni les
 * champs, ni les aggs, ni les noms de filtres d'un cas d'usage : tout arrive du
 * YAML (`filters`, `query`, `shape`, `kpiCards`).
 *
 * Pourquoi une primitive (et pas un `ReactiveJsonSubroot`) : un subroot
 * reconstruit son `dataOverride` à chaque rendu, et `ReactiveJsonRoot` cale son
 * effet de fetch sur l'IDENTITÉ de cet objet — donc n'importe quel rendu non
 * lié (un `updateData` de resize, un `setData` de dropdown) vide le store du
 * subroot (flash blanc) et relance tous les GET. Ici l'effet dépend des VALEURS
 * des filtres (`JSON.stringify`), donc un graphe ne refetche que quand SES
 * filtres changent — jamais sur l'interaction d'un voisin. La donnée précédente
 * reste à l'écran pendant le fetch (pas de flash).
 *
 * Fetch GET only (pas de CSRF). Les valeurs multiples deviennent des paramètres
 * répétés `filter[champ][]=` — le back en fait une clause ES `terms` (OU). Une
 * entrée peut être une enveloppe `{ value, label }` (forme produite par
 * l'éditeur multi-sélection `es-select`) : seule la `value` part dans l'URL.
 *
 * YAML — graphe (mode déclaratif, à privilégier) :
 *   - type: ChartLoader
 *     url: "/api/elasticsearch/deal_transitions/config/pipeline_snapshot"
 *     filters:                       # clés = champs ES littéraux (libres)
 *       pipeline_name: ~~.…data.pipeline
 *       bu_label: ~~.…data.bus       # tableau → filter[bu_label][]= répétés
 *       es_group_grants:             # forme longue : préfixe constant du CHAMP
 *         from: ~~.…data.bu          #   valeur stockée = 3
 *         prefix: "group:"           #   valeur envoyée = group:3
 *       pipeline_name:               # forme longue : opérateur d'ensemble
 *         from: ~~.…data.excluded    #   none → filter[pipeline_name][none][]=
 *         operator: none             #   any (défaut) | all | none
 *     missing: ~~.…data.missing      # contrôle de saisie : ces champs doivent
 *                                    # être ABSENTS (filter[f][not_exists]=1)
 *     past: ~~.…data.past            # ces champs date doivent être DÉPASSÉS
 *                                    # (filter[f][lt]=now)
 *     query:                         # jetons [query:x] de la requête stockée
 *       interval: month
 *     shape:                         # cf. esAggToChart.shapeFromSpec
 *       buckets: aggregations.snapshots.buckets
 *       bucketKeys: [s0, s1, s2, s3, s4, s5]
 *       label: { format: monthsAgo }
 *       series: { from: by_stage, labels: aggregations.by_stage_labels }
 *     chartType: bar                 # bar (défaut) | line | doughnut
 *     stacked: true
 *     stackTotals: true              # total de chaque pile au-dessus de la barre
 *     sliceLabels: true              # « 55,56 % (20) » sur chaque part (camembert)
 *     totalLabel: "Total (Nombre) transactions"   # total en haut à droite
 *     legendPosition: bottom
 *     xAxisTitle: "Date de création — Mensuel"
 *     yAxisTitle: "Nombre de transactions"
 *     valueSuffix: " €"
 *     precisionZero: true            # ticks Y entiers
 *     richTooltip: true
 *     fadeOnHover: true
 *     heightClass: "relative h-80"   # optionnel — par défaut la zone de graphe
 *                                    # est élastique (.chart-fill) et suit la
 *                                    # taille du widget
 *     ariaLabel: "…"
 *     emptyText: "Aucune donnée sur la période"
 *
 * YAML — tableau (même `shape:`, rendu en `<table>`) :
 *   - type: ChartLoader
 *     variant: table
 *     url: "/api/elasticsearch/deals/config/deals_breakdown"
 *     shape: { buckets: aggregations.by_owner.buckets, label: {…} }
 *     labelHeader: "Propriétaire de la transaction"
 *     valueHeader: "(Nombre) transactions"
 *     totalRowLabel: "Total du rapport"    # total en haut à droite ; "none" le supprime
 *
 * YAML — KPIs :
 *   - type: ChartLoader
 *     variant: kpis
 *     url: "/api/elasticsearch/deals/config/deals_list"
 *     kpiCards:
 *       - { label: "Transactions", path: pagination.total }
 *       - { label: "CA réalisé", path: aggregations.revenue_by_year.per_year.buckets,
 *           reduce: sum, of: revenue.value, format: currency }
 *     cardChrome: false              # CARTE SIMPLE : pas de tuile encastrée —
 *                                    # le chiffre occupe la carte qui l'entoure
 *                                    # (celle du widget), centré. Défaut : true
 *                                    # (grille de tuiles `.stat-card`), qui reste
 *                                    # le bon rendu pour PLUSIEURS chiffres.
 *
 *
 * YAML — DÉTAIL AU CLIC (tableau croisé ou graphe) :
 *   `selectionPath` désigne un emplacement du STORE. Un clic sur une cellule ou
 *   sur un segment y écrit les coordonnées du point :
 *     { rowIndex, rowFromEnd, rowKey, rowLabel, seriesKey, seriesLabel, value, open }
 *   `rowFromEnd` compte depuis la FIN (0 = dernière colonne) : c'est ce que veut
 *   une fenêtre « les N derniers mois ». `seriesKey` (la série : une barre
 *   empilée) et `rowKey` (la ligne : une part de camembert) sont les clés BRUTES
 *   de l'agrégation (un tid), pas leurs libellés — ce sont elles qui refiltrent.
 *   Le reste est déjà déclaratif : `Modal` s'ouvre sur `~~.sel.open`, et un
 *   SECOND ChartLoader filtré sur `~~.sel.*` va chercher le détail.
 *     - type: ChartLoader
 *       variant: table
 *       selectionPath: ~~.sel
 *
 * YAML — documents (variant: rows) :
 *   Les autres variantes lisent des agrégations ; celle-ci liste les documents
 *   eux-mêmes (`contents[]`), colonnes déclarées. C'est le contenu d'une modale
 *   de détail.
 *     - type: ChartLoader
 *       variant: rows
 *       url: "/api/elasticsearch/deal_values/config/pipeline_amount_details"
 *       columns:
 *         - { label: "Transaction", path: deal_title, href: "/crm/deals/:deal_nid", target: _blank }
 *         - { label: "Montant", path: amount, format: number }
 *         - { label: "Depuis", path: entered_at, format: date }
 *   `href` : gabarit à jetons `:champ` résolus sur la ligne ; la cellule devient
 *   un lien (texte simple si un jeton est vide). `target: _blank` l'ouvre dans
 *   un nouvel onglet, annoncé aux lecteurs d'écran.
 *   `nowrap: true` (sur le ChartLoader) : aucune cellule ne passe à la ligne ; le
 *   tableau défile en largeur. Vaut aussi pour `variant: table`.
 *   `sortable: true` (sur le ChartLoader) : un clic sur un en-tête trie la colonne,
 *   un second inverse l'ordre ; tri en mémoire, selon le `format` de la colonne.
 * LEGACY : `variant: count|amount|revenue` route encore la réponse vers
 * `crmDashboardProcessor` (formes CRM figées). Gelé — aucun nouveau variant :
 * les nouveaux graphes passent par `shape:`.
 */

// Legacy : variant → clé de graphe dans `result.charts` (crmDashboardProcessor).
const CHART_KEY = {
  count: 'byStagePerMonth',
  amount: 'byStageAmountPerMonth',
  revenue: 'revenueByYear',
}

const isEmpty = (v) => v === undefined || v === null || v === ''

/**
 * Opérateurs d'ensemble acceptés par la forme longue `{ from, operator }`.
 * Liste blanche : le back ne connaît que ces trois-là (`buildAdvancedBooleanClause`).
 */
const SET_OPERATORS = ['any', 'all', 'none']

/**
 * Déballe une valeur de filtre : l'éditeur multi-sélection du builder stocke des
 * enveloppes `{ value, label }` (idem `object-ref`) pour que le YAML puisse
 * afficher un libellé lisible — seule la `value` part dans la requête.
 */
const filterValue = (v) =>
  v && typeof v === 'object' && !Array.isArray(v) ? v.value : v

/**
 * Construit l'URL GET : `filter[<champ>]=` pour les scalaires,
 * `filter[<champ>][]=` répétés pour les tableaux, `filter[<champ>][<op>][]=`
 * quand la forme longue déclare un `operator`, `es_query[<nom>]=` pour les
 * jetons `[query:nom]` de la requête stockée. Les valeurs vides sont omises
 * (un filtre non renseigné ne doit pas restreindre la requête).
 */
function buildUrl(base, filters, query, prefixes = {}, operators = {}, missing = [], past = []) {
  const parts = []
  const add = (k, raw, prefix) => {
    const v = filterValue(raw)
    if (isEmpty(v)) return
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(`${prefix ?? ''}${v}`)}`)
  }
  // Contrôles de SAISIE : ici le champ est la DONNÉE (choisie dans le panneau
  // de filtres), pas une clé figée du YAML — d'où deux listes de noms de champs
  // plutôt que des entrées de `filters:`. Le back sait déjà en faire une clause
  // (`{not_exists:true}` → `bool.must_not.exists`, `{lt}` → `range`) : rien à
  // ajouter côté PHP.
  missing.forEach((f) => add(`filter[${filterValue(f)}][not_exists]`, 1))
  past.forEach((f) => add(`filter[${filterValue(f)}][lt]`, 'now'))
  Object.entries(filters || {}).forEach(([field, value]) => {
    const prefix = prefixes[field]
    // Opérateur d'ensemble : `filter[champ][none][]=` — le back en fait un
    // `bool.must_not.terms` (idem `any` → OU, `all` → ET sur multivalué). Sans
    // opérateur, la forme historique `filter[champ][]=` (un OU) est conservée.
    const op = operators[field]
    if (op) {
      const list = Array.isArray(value) ? value : [value]
      list.forEach((v) => add(`filter[${field}][${op}][]`, v, prefix))
    }
    else if (Array.isArray(value)) value.forEach((v) => add(`filter[${field}][]`, v, prefix))
    else add(`filter[${field}]`, value, prefix)
  })
  Object.entries(query || {}).forEach(([name, value]) => add(`es_query[${name}]`, value))
  return parts.length ? `${base}?${parts.join('&')}` : base
}

/**
 * Tableau de DOCUMENTS (variant: rows), extrait de ChartLoader pour porter son
 * propre état : le tri est un état React, et un hook ne peut pas vivre dans une
 * branche conditionnelle du parent — ChartLoader rend ses variantes par retours
 * anticipés.
 *
 * Tri (`sortable`) : dans le navigateur, sur les lignes déjà chargées. La
 * requête en ramène une page (≤ 200) ; trier en mémoire évite un aller-retour.
 * Un clic sur un en-tête trie, un second inverse. Chaque colonne se compare
 * selon son `format` (nombre, date, sinon texte en ordre alphabétique français,
 * chiffres compris) ; les cellules vides restent EN FIN de liste dans les deux
 * sens — un tri décroissant ne doit pas commencer par des « — ».
 */
function RowsTable({ rows, cols, suffix, ariaLabel, emptyText, nowrap, sortable, loading }) {
  const [sort, setSort] = useState({ col: null, dir: 'asc' })

  // L'enveloppe `raw` peut rendre des tableaux (search_api indexe en
  // multivalué) : une colonne `deal_title` doit afficher le titre, pas `["…"]`.
  const first = (row, c) => {
    const v = getPath(row, c.path)
    return Array.isArray(v) ? v[0] : v
  }
  const blank = (v) => v == null || v === ''

  const sorted = useMemo(() => {
    const c = sort.col == null ? null : cols[sort.col]
    if (!sortable || !c) return rows
    const factor = sort.dir === 'asc' ? 1 : -1
    const collator = new Intl.Collator('fr', { sensitivity: 'base', numeric: true })
    return [...rows].sort((a, b) => {
      const va = first(a, c)
      const vb = first(b, c)
      if (blank(va) || blank(vb)) return blank(va) === blank(vb) ? 0 : (blank(va) ? 1 : -1)
      if (c.format === 'number' || c.format === 'date') return (Number(va) - Number(vb)) * factor
      return collator.compare(String(va), String(vb)) * factor
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, cols, sortable, sort])

  const toggle = (i) => setSort((s) => (s.col === i
    ? { col: i, dir: s.dir === 'asc' ? 'desc' : 'asc' }
    : { col: i, dir: 'asc' }))

  const cell = (row, c) => {
    const val = first(row, c)
    if (c.format === 'number') return `${(Number(val) || 0).toLocaleString('fr-FR')}${suffix}`
    if (c.format === 'date') return val ? new Date(Number(val) * 1000).toLocaleDateString('fr-FR') : '—'
    return blank(val) ? '—' : String(val)
  }
  const numeric = (c) => c.format === 'number'
  // `href` : gabarit à jetons `:champ` (la convention de PathLink), résolus sur
  // la ligne — `href: "/crm/deals/:deal_nid"`. Aucune URL n'est écrite ici :
  // le YAML décide où mène chaque colonne. Un jeton vide donne du texte simple,
  // jamais un lien cassé. `data-builder-allow-navigation` : en édition, le
  // builder neutralise les liens d'un bloc — et une modale en portail lui reste
  // rattachée par l'arbre React, donc son clic passe quand même par lui.
  const hrefOf = (row, c) => {
    if (typeof c.href !== 'string' || c.href === '') return null
    let missing = false
    const url = c.href.replace(/:([A-Za-z_][A-Za-z0-9_.]*)/g, (_m, field) => {
      const v = getPath(row, field)
      const val = Array.isArray(v) ? v[0] : v
      if (blank(val)) { missing = true; return '' }
      return encodeURIComponent(String(val))
    })
    return missing ? null : url
  }
  const linked = (row, c) => {
    const text = cell(row, c)
    const href = hrefOf(row, c)
    if (!href) return text
    const newTab = c.target === '_blank'
    return (
      <a
        href={href}
        target={newTab ? '_blank' : undefined}
        rel={newTab ? 'noopener noreferrer' : undefined}
        className="font-medium text-iris-600 hover:underline"
        data-builder-allow-navigation=""
      >
        {text}
        {newTab ? <span className="sr-only"> (nouvel onglet)</span> : null}
      </a>
    )
  }

  // En-tête triable : un vrai <button> (clavier, lecteur d'écran) ; l'ordre est
  // porté par `aria-sort`, sur la SEULE colonne triée (motif ARIA « sortable
  // table »). Les flèches sont décoratives : `aria-hidden`.
  const header = (c, i) => {
    if (!sortable) return c.label
    const active = sort.col === i
    const Icon = !active ? ChevronsUpDown : (sort.dir === 'asc' ? ArrowUp : ArrowDown)
    return (
      <button type="button" className="th-sort" onClick={() => toggle(i)}>
        {c.label}
        <Icon size={12} aria-hidden="true" className={active ? undefined : 'th-sort-idle'} />
      </button>
    )
  }
  const ariaSort = (i) => (sortable && sort.col === i
    ? (sort.dir === 'asc' ? 'ascending' : 'descending')
    : undefined)

  return (
    <div className="table-wrap table-wrap-tall">
      <table className={nowrap ? 'table table-nowrap' : 'table'}>
        <caption className="sr-only">{ariaLabel}</caption>
        <thead>
          <tr>{cols.map((c, i) => (
            <th key={i} scope="col" aria-sort={ariaSort(i)} className={numeric(c) ? 'text-right' : undefined}>{header(c, i)}</th>
          ))}</tr>
        </thead>
        <tbody>
          {sorted.length ? sorted.map((row, i) => (
            <tr key={row?.id ?? i}>{cols.map((c, j) => (
              <td key={j} className={numeric(c) ? 'text-right tabular-nums' : undefined}>{linked(row, c)}</td>
            ))}</tr>
          )) : (
            <tr><td colSpan={cols.length || 1} className="text-sm text-slate-400">{loading ? '…' : emptyText}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

export const ChartLoader = ({ props, path, datafield }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const url = resolve(props.url)
  const variant = resolve(props.variant) || (props.shape ? 'shape' : 'count')
  const shape = resolve(props.shape)

  // Les filtres sont un dictionnaire LIBRE : chaque clé est un champ ES, chaque
  // valeur un template résolu ici (scalaire ou tableau).
  //
  // Forme longue `{ from, prefix }` : certains champs d'index stockent un jeton
  // PRÉFIXÉ (`es_group_grants` = `group:<gid>`) alors qu'un picker de
  // référentiel ne connaît que l'identifiant nu. Le préfixe est une constante du
  // CHAMP, pas de la donnée — il vit donc ici, dans le YAML, et pas dans un
  // processor qui devrait connaître le cas d'usage.
  const filters = {}
  const prefixes = {}
  const operators = {}
  Object.entries(props.filters || {}).forEach(([k, v]) => {
    if (v && typeof v === 'object' && !Array.isArray(v) && v.from !== undefined) {
      filters[k] = resolve(v.from)
      if (v.prefix) prefixes[k] = String(v.prefix)
      if (SET_OPERATORS.includes(v.operator)) operators[k] = v.operator
    }
    else { filters[k] = resolve(v) }
  })
  const query = {}
  Object.entries(props.query || {}).forEach(([k, v]) => { query[k] = resolve(v) })

  // `missing:` / `past:` — les deux contrôles de SAISIE, en listes de champs ES
  // (cf. buildUrl). Une liste vide ne restreint rien.
  const toList = (v) => {
    const r = resolve(v)
    if (isEmpty(r)) return []
    return (Array.isArray(r) ? r : [r]).filter((x) => !isEmpty(filterValue(x)))
  }
  const missing = toList(props.missing)
  const past = toList(props.past)

  // Signature de VALEURS (pas d'identité d'objet) → dépendance stable de l'effet.
  const filterKey = JSON.stringify({ url, filters, query, prefixes, operators, missing, past })

  const [raw, setRaw] = useState(null)
  const headers = globalDataContext.headersForRjBuild
  // Garde la donnée précédente à l'écran pendant un refetch (pas de flash).
  const inFlight = useRef(null)

  useEffect(() => {
    let alive = true
    const full = buildUrl(url, filters, query, prefixes, operators, missing, past)
    inFlight.current = full
    fetch(full, { method: 'GET', credentials: 'include', headers: headers || {} })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => {
        if (!alive || inFlight.current !== full) return
        setRaw({ url: full, data })
      })
      .catch(() => { /* garde la donnée précédente ; état vide géré au rendu */ })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey])

  // --- KPIs ---
  // Cartes = CONTENU fourni par le YAML. Chaque carte est soit déclarative
  // (`path`/`reduce`/`format`, évaluée sur la réponse brute), soit legacy
  // (`key` dans les KPIs pré-calculés par crmDashboardProcessor).
  if (variant === 'kpis') {
    const cards = resolve(props.kpiCards) || []
    const legacy = cards.some((c) => c && c.key)
      ? legacyShape(raw)?.kpis || {}
      : {}
    const valueOf = (c) => (c.key ? (legacy[c.key] ?? '—') : evaluateKpi(raw?.data, c))
    // CARTE SIMPLE (`cardChrome: false`) : un seul chiffre, au centre de la
    // carte du widget. Une tuile `.stat-card` dans une carte, c'est une carte
    // dans une carte — le chiffre y flotte en haut à gauche d'une grande zone
    // vide, et son intitulé s'y tronque. Recettes DS `num-display` + `stat-label`,
    // rien d'ad hoc.
    if (resolve(props.cardChrome) === false) {
      return (
        <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-2 text-center">
          {cards.map((c, i) => (
            <div key={i} className="flex flex-col items-center gap-1.5">
              <div className="num-display">{valueOf(c)}</div>
              {c.label ? <div className="stat-label">{c.label}</div> : null}
            </div>
          ))}
        </div>
      )
    }
    // Une rangée équilibrée : 6 cartes se lisent en 3x2, pas en 4+2. Au-delà,
    // 4 colonnes restent le repli.
    const cols = cards.length % 3 === 0 && cards.length <= 6
      ? '@2xl:grid-cols-3'
      : cards.length === 2 ? '@2xl:grid-cols-2' : '@2xl:grid-cols-4'
    return (
      <div className={`grid grid-cols-1 ${cols} gap-4 w-full content-start`}>
        {cards.map((c, i) => (
          <div className="stat-card" key={i}>
            <div className="stat-label">{c.label}</div>
            <div className="stat-value">{valueOf(c)}</div>
          </div>
        ))}
      </div>
    )
  }

  // `nowrap: true` : aucune cellule ne passe à la ligne (recette DS
  // `.table-nowrap`). Trop large, le tableau défile alors en largeur dans son
  // `.table-wrap` au lieu de s'écraser — plus lisible pour des lignes de
  // données (un titre, un montant, une date par colonne). Vaut pour `rows`
  // comme pour `table`.
  const nowrap = Boolean(resolve(props.nowrap))

  // --- Documents (variant: rows) ---
  // Les autres variantes lisent des AGRÉGATIONS ; celle-ci liste les documents
  // eux-mêmes (`contents[]` de l'enveloppe unifiée), colonnes déclarées en YAML.
  // C'est ce qui remplit une modale de détail : « quelles lignes composent ce
  // chiffre ». Placé AVANT le calcul de `data` : sans `shape:`, celui-ci
  // passerait par le processeur CRM hérité, qui n'a rien à faire ici. Le rendu
  // vit dans `RowsTable`, qui porte l'état du tri.
  if (variant === 'rows') {
    return (
      <RowsTable
        rows={raw?.data?.contents ?? []}
        cols={resolve(props.columns) || []}
        suffix={resolve(props.valueSuffix) || ''}
        ariaLabel={resolve(props.ariaLabel) || ''}
        emptyText={resolve(props.emptyText) || 'Aucune donnée'}
        nowrap={nowrap}
        sortable={Boolean(resolve(props.sortable))}
        loading={raw == null}
      />
    )
  }

  // --- Graphes ---
  // Mode déclaratif (`shape:`) : le YAML décrit buckets/labels/séries ; sinon
  // repli sur les formes CRM figées du processor (legacy `variant`).
  const data = shape
    ? shapeFromSpec(raw?.data, shape)
    : legacyShape(raw)?.charts?.[CHART_KEY[variant] || 'byStagePerMonth']

  const hasData = !!(data && Array.isArray(data.labels) && data.labels.length && data.datasets?.length)
  const chartType = resolve(props.chartType) || 'bar'
  // Élastique par défaut : la zone de graphe prend la hauteur restante de la
  // carte (widget de tableau de bord redimensionnable) et retombe sur un ratio
  // 16/9 quand rien ne la contraint. `heightClass` reste une échappatoire pour
  // imposer une hauteur fixe (ex. "relative h-80").
  const heightClass = resolve(props.heightClass) || 'chart-fill'
  const ariaLabel = resolve(props.ariaLabel) || ''
  const emptyText = resolve(props.emptyText) || 'Aucune donnée sur la période'
  const totalLabel = resolve(props.totalLabel)
  // Ligne de total au-dessus du graphe (`totalLabel`) : la somme de ce qui est
  // RÉELLEMENT dessiné — pas le `total` de pagination —, donc elle concorde
  // toujours avec les parts affichées, filtres compris.
  const total = (data?.datasets ?? []).reduce(
    (acc, ds) => acc + (ds.data ?? []).reduce((a, v) => a + (Number(v) || 0), 0),
    0,
  )

  // --- Sélection d'une cellule / d'un segment ---
  // `selectionPath` désigne un emplacement du STORE ; y écrire les coordonnées
  // du point cliqué suffit à tout le reste : une modale s'ouvre en `hide`/
  // `setData` (patron de Navigation.yaml) et un SECOND ChartLoader, filtré sur
  // ces coordonnées, va chercher le détail. Aucune mécanique de fetch en plus —
  // celle-ci refetche déjà sur les VALEURS de ses filtres.
  //
  // `rowFromEnd` est la position depuis la FIN : c'est ce que consomment les
  // fenêtres « les N derniers mois », dont la dernière ligne est le mois
  // courant. Le YAML ne sait pas soustraire, il faut donc le lui donner.
  const selectionPath = props.selectionPath
  const selectable = typeof selectionPath === 'string' && selectionPath !== ''
  const emitSelection = (rowIndex, seriesIndex) => {
    if (!selectable) return
    const ds = data?.datasets?.[seriesIndex]
    // Série « reste » (`otherLabel` : « non renseigné ») : aucun filtre ne la
    // désigne — le détail listerait TOUT le mois. Même règle que les cellules
    // du variant `table`, qui ne sont pas cliquables dans ce cas.
    if (!ds || ds.rest) return
    // Chemin résolu par reactive-json LUI-MÊME, comme le font IntegerField et
    // DateRangeAutoInterval. Pour `~~.x`, retirer le préfixe à la main tombait
    // juste (le store racine ignore `data.`) ; pour `~.x`, relatif au GABARIT
    // courant, il visait la racine — faux dès que le ChartLoader vit dans un
    // template.
    const { formDataPath: target } = propsDataLocationToPathAndValue({
      currentPath: path,
      datafield,
      dataLocation: selectionPath,
      defaultValue: null,
      globalDataContext,
      templateContext,
    })
    if (!target) return
    globalDataContext.updateData({
      rowIndex,
      rowFromEnd: (data.labels?.length ?? 0) - 1 - rowIndex,
      rowKey: data.rowKeys?.[rowIndex] ?? '',
      rowLabel: data.labels?.[rowIndex] ?? '',
      seriesKey: ds.key ?? '',
      seriesLabel: ds.label ?? '',
      value: ds.data?.[rowIndex] ?? 0,
      // Booléen VRAI, pas la chaîne « true » : `Modal` teste `Boolean(valeur)`,
      // et sa croix de fermeture réécrit un `false` booléen.
      open: true,
    }, target)
  }

  // --- Table ---
  // Même `shape:` que les graphes, rendu en tableau : une ligne par libellé,
  // une colonne par série (une seule → l'en-tête est `valueHeader`), plus le
  // total en haut à droite (même place que `totalLabel` sur un graphe). Sert
  // les widgets « contrôle de saisie », où le chiffre se lit mieux qu'une barre.
  if (variant === 'table') {
    const cols = data?.datasets ?? []
    const suffix = resolve(props.valueSuffix) || ''
    const fmt = (v) => `${(Number(v) || 0).toLocaleString('fr-FR')}${suffix}`
    const labelHeader = resolve(props.labelHeader) || 'Libellé'
    const valueHeader = resolve(props.valueHeader) || 'Nombre'
    const totalRowLabel = resolve(props.totalRowLabel) || 'Total du rapport'
    // Total en haut à droite, comme `totalLabel` sur un graphe : la somme de ce
    // qui est RÉELLEMENT affiché, par série.
    const totals = cols.map((ds) => (ds.data ?? []).reduce((a, v) => a + (Number(v) || 0), 0))
    return (
      <div className="flex flex-col h-full min-h-0">
        {hasData && totalRowLabel !== 'none' ? (
          <div className="flex-none pb-1 text-right text-xs font-semibold text-slate-600">
            {cols.length > 1
              ? `${totalRowLabel} : ${cols.map((ds, j) => `${ds.label} ${fmt(totals[j])}`).join(' · ')}`
              : `${totalRowLabel} : ${fmt(totals[0])}`}
          </div>
        ) : null}
        <div className="table-wrap flex-1 min-h-0 overflow-y-auto">
          <table className={nowrap ? 'table table-nowrap' : 'table'}>
            <caption className="sr-only">{ariaLabel}</caption>
            <thead>
              <tr>
                <th scope="col">{labelHeader}</th>
                {(cols.length ? cols : [null]).map((ds, i) => (
                  <th key={i} scope="col" className="text-right">
                    {cols.length > 1 ? ds.label : valueHeader}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {hasData ? data.labels.map((l, i) => (
                <tr key={i}>
                  <td>{l}</td>
                  {cols.map((ds, j) => (
                    <td key={j} className="text-right">
                      {/* Cellule CLIQUABLE dès que `selectionPath` est déclaré, et
                          seulement quand elle porte une valeur hors série « reste » :
                          ouvrir un détail vide, ou un détail qu'on ne sait pas
                          filtrer, ne rend service à personne. */}
                      {selectable && !ds.rest && (ds.data?.[i] ?? 0) !== 0 ? (
                        <button
                          type="button"
                          className="cell-drill"
                          onClick={() => emitSelection(i, j)}
                          title={`${ds.label} — ${l} : voir le détail`}
                        >
                          {fmt(ds.data?.[i])}
                        </button>
                      ) : fmt(ds.data?.[i])}
                    </td>
                  ))}
                </tr>
              )) : (
                <tr>
                  <td colSpan={(cols.length || 1) + 1} className="text-sm text-slate-400">
                    {raw == null ? '…' : emptyText}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  const chartBox = (
    <div className={heightClass} role="img" aria-label={ariaLabel}>
      {hasData ? (
        <ChartJs
          props={{
            chartType,
            data,
            legendPosition: resolve(props.legendPosition),
            valueSuffix: resolve(props.valueSuffix),
            precisionZero: resolve(props.precisionZero),
            beginAtZero: resolve(props.beginAtZero),
            richTooltip: resolve(props.richTooltip),
            fadeOnHover: resolve(props.fadeOnHover),
            seriesGroupLabel: resolve(props.seriesGroupLabel),
            stacked: resolve(props.stacked),
            stackTotals: resolve(props.stackTotals),
            sliceLabels: resolve(props.sliceLabels),
            xAxisTitle: resolve(props.xAxisTitle),
            yAxisTitle: resolve(props.yAxisTitle),
            wrapperClass: 'absolute inset-0',
            // Clic sur un segment : mêmes coordonnées, même emplacement de store
            // qu'une cellule de tableau. Un graphe et un tableau nourrissent donc
            // la MÊME modale, sans que le YAML ait à distinguer les deux.
            onSegmentClick: selectable ? emitSelection : undefined,
          }}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-400">
          {raw == null ? '…' : emptyText}
        </div>
      )}
    </div>
  )

  if (!totalLabel || !hasData) return chartBox
  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-none pb-1 text-right text-xs font-semibold text-slate-600">
        {`${totalLabel} : ${total.toLocaleString('fr-FR')}`}
      </div>
      {chartBox}
    </div>
  )
}

/** Legacy : mise en forme CRM figée (gelée — préférer `shape:`). */
function legacyShape(raw) {
  if (!raw) return null
  return crmDashboardProcessor({
    requestContext: { url: raw.url },
    responseContext: { status: 200 },
    dataToProcess: raw.data,
  })
}
